import { mergeSxProps } from "@src/appV2/lib";

import { Button, type ButtonProps, type ButtonSize, type ButtonVariant } from "./Button";
import { Icon, type IconType } from "./Icon";

export interface IconButtonProps extends Omit<
  ButtonProps,
  "startIconType" | "endIconType" | "children"
> {
  size?: ButtonSize;
  variant?: ButtonVariant;
  iconType: IconType;
  /**
   * When true, render as a circle at any size. Defaults to true for `small`
   * (preserves legacy behavior) and false for `medium`/`large`.
   */
  circular?: boolean;
}

/**
 * An icon-only button that reuses Button variants and sizes.
 * Small size is circular by default; medium/large default to square but can
 * opt in to circular via the `circular` prop.
 */
export function IconButton(props: IconButtonProps) {
  const { iconType, size = "medium", circular = size === "small", sx, ...restProps } = props;

  return (
    <Button
      {...restProps}
      size={size}
      sx={mergeSxProps(
        (theme) => ({
          minWidth: "unset",
          ...(size === "small" && {
            padding: theme.size?.button.small.paddingY,
          }),
          ...(size === "medium" && {
            padding: theme.size?.button.medium.paddingY,
          }),
          ...(size === "large" && {
            padding: theme.size?.button.large.paddingY,
          }),
          ...(circular && {
            height: "auto",
            borderRadius: "50%",
            aspectRatio: "1",
          }),
        }),
        sx
      )}
    >
      <Icon type={iconType} />
    </Button>
  );
}
